import { AttributeValueRange, isNonNaNNumber, validAttributeNumberValue } from '../../Validation'
import { objectsMatch } from '../Objects'
import { DayHour, MinuteNumber, WeekDayNumber } from './constants'

/** number of minutes in day -- used for relative UTC period lengths */
export const MINS_HR = 60
/** number of minutes in hour  -- used for relative UTC period lengths */
export const MINS_DAY = 24 * MINS_HR
/** number of minutes in 7 day period  -- used for relative UTC period lengths */
export const MINS_WEEK = 7 * MINS_DAY

export const UTC_HOUR_RANGE: AttributeValueRange = { min: 0, max: 23 }
export const UTC_MIN_RANGE: AttributeValueRange = { min: 0, max: 59 }

export type DayMaskDefinition = {
  sunday: boolean
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
}

/** an all-false dayMask */
export const dayMaskTemplate: DayMaskDefinition = {
  sunday: false,
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
}

export const dayMaskAllTrueTemplate: DayMaskDefinition = {
  sunday: true,
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: true,
}

/** garaunteed correct order days typed as keyof DayMaskDefinition */
export const dayMaskDays: (keyof DayMaskDefinition)[] = Object.keys(dayMaskTemplate) as (keyof DayMaskDefinition)[]

export const getDayMaskDayFromDayNumber = (n: number) => dayMaskDays.at(n % 7) as keyof DayMaskDefinition

export const getDayNumberFromDayMaskKey = (key: keyof DayMaskDefinition) => dayMaskDays.findIndex((entry) => entry === key) as 0 | 1 | 2 | 3 | 4 | 5 | 6

export type UTCPeriodHrMins = {
  utc_start_hour: number
  utc_start_min: number
  duration_mins: number
}

/** enum moved from EntityScheduler types on transfer to @iotv/lfc-devices */
export enum InsertBehavior {
  /** shorten the inserted timeslot if overlaps with next */
  TRUNCATE = 'TRUNCATE',
  /** shift following timeslots if next overlaps */
  SHIFT = 'SHIFT',
}

/** potentially where order is important, eg perhaps getObjectHash with arrays containing dayMasks, ensure keys are in canonical order */
export const canonicalizeDayMask = (dayMask: DayMaskDefinition) =>
  Object.fromEntries(Object.keys(dayMaskTemplate).map((k) => [k, dayMask[k as keyof DayMaskDefinition]]))

/** a utc period with full day mask as used for example in iotv irrigation controller schedules
 * NOTE daym**ask is cased dayMask in payloadDefinition!!! ( in correction 2025 02 02)
 */
export type UTCDayMaskPeriod = UTCPeriodHrMins & {
  dayMask: DayMaskDefinition
}

export type UTCHrMin = { utcHour: DayHour; utcMin: MinuteNumber }

/** a single utc period */
export type UTCDayPeriod = { p: UTCPeriodHrMins; utcDay: WeekDayNumber }

export type UTCSingleTimePoint = { dayMaskDay: keyof DayMaskDefinition } & UTCHrMin

export const isDayMaskDefinition = (x: any): x is DayMaskDefinition => {
  const c = x as DayMaskDefinition
  return !!c && dayMaskDays.every((d) => c[d] === true || c[d] === false)
}

export const isUTCPeriodMins = (x: any): x is UTCPeriodHrMins => {
  const c = (x as UTCPeriodHrMins) ?? {}
  const { duration_mins, utc_start_hour, utc_start_min } = c
  return (
    [duration_mins, utc_start_hour, utc_start_min].every(isNonNaNNumber) &&
    validAttributeNumberValue(utc_start_hour, UTC_HOUR_RANGE).isValid &&
    validAttributeNumberValue(utc_start_min, UTC_MIN_RANGE).isValid
  )
}

export const isUTCDayMaskPeriod = (x: any): x is UTCDayMaskPeriod => {
  const c = (x as UTCDayMaskPeriod) ?? {}
  return isUTCPeriodMins(c) && isDayMaskDefinition(c.dayMask)
}

/** return true if all elements are UTCPeriodHrMins */
export const isUTCPeriodsHrMinsArr = (x: any): x is UTCPeriodHrMins[] => {
  let isValid = false
  if (x instanceof Array) {
    const c = x as UTCPeriodHrMins[]
    isValid = c.every(isUTCPeriodMins)
  }
  return isValid
}

/** return true if all elements are UTCPeriodHrMins and are in asc order */
export const isAscUTCPeriodHrsMinsArr = (x: any): x is UTCPeriodHrMins[] => {
  return (
    isUTCPeriodsHrMinsArr(x) &&
    x.reduce((a, next, i, arr) => {
      if (i > 0) {
        const last = arr[i - 1]
        const lastTo = last.utc_start_hour * MINS_HR + last.utc_start_min + last.duration_mins
        const nextFrom = next.utc_start_hour * MINS_HR + next.utc_start_min
        a = a && lastTo <= nextFrom
      }
      return a
    }, true as boolean)
  )
}

/** stor UTCPeriodHrMins by start time */
export const sortUTCPeriodHrMins = (x1: UTCPeriodHrMins, x2: UTCPeriodHrMins): -1 | 1 => {
  const x1DayMins = x1.utc_start_hour * MINS_HR + x1.utc_start_min
  const x2DayMins = x2.utc_start_hour * MINS_HR + x2.utc_start_min
  return x1DayMins < x2DayMins ? -1 : 1
}

/** sort UTCDayPeriod by day and if same day by start time */
export const sortUTCDayPeriods = (x1: UTCDayPeriod, x2: UTCDayPeriod): -1 | 1 => {
  if (x1.utcDay !== x2.utcDay) {
    return x1.utcDay < x2.utcDay ? -1 : 1
  } else {
    return sortUTCPeriodHrMins(x1.p, x2.p)
  }
}

export const utcDayMaskPeriodToUtcDayPeriods = (utcDayMaskPeriod: UTCDayMaskPeriod): UTCDayPeriod[] => {
  const { dayMask, ...uTCPeriodHrMins } = utcDayMaskPeriod
  return Object.entries(dayMask)
    .filter(([dayKey, isOn]) => !!isOn)
    .map(([dayKey, isOn]) => {
      const utcDayPeriod: UTCDayPeriod = {
        p: uTCPeriodHrMins,
        utcDay: getDayNumberFromDayMaskKey(dayKey as keyof DayMaskDefinition),
      }
      return utcDayPeriod
    })
}

/** convert utcDayPeriods of equal start time but differing days to utcDayMaskPeriod, eg where time has been shifted and days may not equal original utcDayMaskPeriod days
 * @throws Error when utcDayPeriods is empty or utcDayPeriods don't have matching
 */
export const utcDayPeriodsToUtcDayMaskPeriod = (utcDayPeriods: UTCDayPeriod[]): UTCDayMaskPeriod => {
  if (utcDayPeriods.length > 0) {
    const clonedDayPeriods = [...utcDayPeriods]
    clonedDayPeriods.sort(sortUTCDayPeriods)
    const initialUTCPeriodHrMins = clonedDayPeriods.at(0)!.p
    if (clonedDayPeriods.every((c) => objectsMatch(c.p, initialUTCPeriodHrMins))) {
      const { duration_mins, utc_start_hour, utc_start_min } = initialUTCPeriodHrMins
      const utcDayMaskPeriod: UTCDayMaskPeriod = { dayMask: dayMaskTemplate, duration_mins, utc_start_hour, utc_start_min }
      for (let period of clonedDayPeriods) {
        const dayMaskKey = getDayMaskDayFromDayNumber(period.utcDay)
        utcDayMaskPeriod.dayMask[dayMaskKey] = true
      }

      return utcDayMaskPeriod
    } else {
      throw new Error(`utcDayPeriodsToUtcDayMaskPeriod must be provided utcDayPerids where each utcPeriodHrdMins are equal`)
    }
  } else {
    throw new Error(`utcDayPeriodsToUtcDayMaskPeriod nust be provided one or more UTCDayPeriods`)
  }
}

export const utcToLocalTime = (utcHour: DayHour, utcMin: MinuteNumber, utcMinsOffset: number): { localHr: DayHour; localMin: MinuteNumber } => {
  const utcMinsFromDayStart = (24 + utcHour) * MINS_HR + utcMin
  const local = utcMinsFromDayStart + utcMinsOffset

  const localHr = (Math.floor(local / MINS_HR) % 24) as DayHour
  const localMin = (local % MINS_HR) as MinuteNumber

  return { localHr, localMin }
}

export const localTimeToUTC = (localHr: DayHour, localMin: MinuteNumber, utcMinsOffset: number): { utcHour: DayHour; utcMin: MinuteNumber } => {
  const utcMinsFromDayStart = (24 + localHr) * MINS_HR + localMin
  const local = utcMinsFromDayStart - utcMinsOffset

  const utcHour = (Math.floor(local / MINS_HR) % 24) as DayHour
  const utcMin = (local % MINS_HR) as MinuteNumber

  return { utcHour, utcMin }
}
